<template>
  <Dialog :open="props.show" @update:open="onClose">
    <DialogContent
      class="!flex !flex-col w-[343px] max-w-[calc(100vw-1rem)] max-h-[90vh] gap-0 overflow-hidden border border-[#EAEEF0] bg-white p-0 shadow-[0_4px_6px_-2px_rgba(16,24,40,0.03),0_12px_16px_-4px_rgba(16,24,40,0.08)] sm:rounded-[12px]"
    >
      <StepperHeader v-if="!generalStore.iFrame" :current-step="currentStep" :steps="steps" />
      <DialogHeader class="!text-center">
        <DialogTitle>{{ titleByTab }}</DialogTitle>
        <DialogDescription class="text-[#256E8A] px-4">{{ descriptionByTab }}</DialogDescription>
      </DialogHeader>

      <div class="min-h-0 flex-1 overflow-y-auto px-4 pb-4 sm:px-6">
        <SectionDivider />
        <CreateAmendment
          v-if="currentTab === 'Edit' && props.order"
          :order="props.order"
          :end-date="props.order.endDateMax ?? ''"
          :travel-date="props.order.travelDate ?? ''"
          :use-travel-date="true"
          @preview-created="onPreviewCreated"
        />
        <ViewAmendment
          v-if="currentTab === 'Draft'"
          :preview="amendmentPreview"
          :order="props.order"
          :is-creating="props.isCreating"
          @back="onBackToEdit"
          @close="onClosePreview"
          @view-summary="onViewSummary"
        />
        <viewPDFSummary
          v-if="currentTab === 'Amend'"
          :preview="amendmentPreview"
          :order="props.order"
          :should-accept-on-confirm="!props.isCreating"
          @back="onBackToDraft"
          @confirm="onConfirmAmendment"
        />

        <PoweredBy />
      </div>
    </DialogContent>
  </Dialog>
</template>
<script setup lang="ts">
import PoweredBy from '@/components/common/PoweredBy.vue';
import SectionDivider from '@/components/common/SectionDivider.vue';
import StepperHeader from '@/components/common/StepperHeader.vue';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import DialogDescription from '@/components/ui/dialog/DialogDescription.vue';
import DialogHeader from '@/components/ui/dialog/DialogHeader.vue';
import DialogTitle from '@/components/ui/dialog/DialogTitle.vue';
import { acceptAmendmentPreview, getAmendmentPreviewById } from '@/services/amendment';
import { useGeneralStore } from '@/stores/general';
import { createException } from '@/utils/monitoring.utils';
import { computed, ref, watch } from 'vue';
import type { ModelOrderAmendmentPreviewResponse, ModelOrderResponse } from '@layup/api-sdk';
import CreateAmendment from '../create-order-amendment/CreateAmendment.vue';
import ViewAmendment from '../create-order-amendment/view-amendment/ViewAmendment.vue';
import viewPDFSummary from '../create-order-amendment/view-amendment/viewPDFSummary.vue';

const generalStore = useGeneralStore();
const emit = defineEmits<{
  (e: 'update:show', value: boolean): void;
  (e: 'refresh-order'): void;
}>();
const currentStep = ref(0);
const props = defineProps<{
  show: boolean;
  order: ModelOrderResponse | undefined;
  isCreating: boolean;
}>();
const currentTab = ref<'Edit' | 'Draft' | 'Amend'>('Edit');
const amendmentPreview = ref<ModelOrderAmendmentPreviewResponse | null>(null);
const shouldRefreshOrderOnClose = ref(false);

const steps = ref([
  { name: 'Edit', number: '01' },
  { name: 'Draft', number: '02' },
  { name: 'Amend', number: '03' },
]);

const titleByTab = computed(() => {
  if (currentTab.value === 'Draft') return 'Preview New Plan';
  if (currentTab.value === 'Amend') return null;
  return 'Amend Payment Plan';
});

const descriptionByTab = computed(() => {
  if (currentTab.value === 'Draft') return 'Review changes before confirming the amendment.';
  if (currentTab.value === 'Amend') return null;
  return 'Edit the fields below to adjust the plan.';
});

const onClose = (open: boolean) => {
  if (!open) {
    currentTab.value = 'Edit';
    currentStep.value = 0;
    amendmentPreview.value = null;

    if (shouldRefreshOrderOnClose.value) {
      shouldRefreshOrderOnClose.value = false;
      emit('refresh-order');
    }
  }
  emit('update:show', open);
};

const onClosePreview = () => {
  if (props.isCreating) {
    shouldRefreshOrderOnClose.value = true;
  }
  onClose(false);
};

const onPreviewCreated = (preview: ModelOrderAmendmentPreviewResponse) => {
  amendmentPreview.value = preview;
  currentTab.value = 'Draft';
  currentStep.value = 1;
};

const onBackToEdit = () => {
  currentTab.value = 'Edit';
  currentStep.value = 0;
};

const onBackToDraft = () => {
  currentTab.value = 'Draft';
  currentStep.value = 1;
};

const onConfirmAmendment = () => {
  if (props.isCreating) {
    shouldRefreshOrderOnClose.value = true;
    onClose(false);
    return;
  }

  shouldRefreshOrderOnClose.value = true;

  // Keep the modal open after confirm in confirm-amendment flow so success state remains visible.
};

const onViewSummary = (preview: ModelOrderAmendmentPreviewResponse | null) => {
  amendmentPreview.value = preview;
  currentTab.value = 'Amend';
  currentStep.value = 2;
};

watch(
  () => props.show,
  async (show) => {
    if (!show) return;

    if (props.isCreating) {
      currentTab.value = 'Edit';
      currentStep.value = 0;
      amendmentPreview.value = null;
    } else if (props.order?.amendmentPreviewId) {
      currentTab.value = 'Draft';
      currentStep.value = 1;

      try {
        var response = await getAmendmentPreviewById(props.order.amendmentPreviewId);
        if (!props.show || props.isCreating || props.order.amendmentPreviewId != response._id) return;
        amendmentPreview.value = response;
      } catch {
        // TODO: Log and show error message

        currentTab.value = 'Edit';
        currentStep.value = 0;
        amendmentPreview.value = null;
      }
    }
  },
);
</script>
